import { createPredicate } from './condition.js';
import { BrandclubEventType } from './types.js';

//
let initialized = false;
let debug = false;
const PROVIDER = 'Meta';
let emit = true;
let identity = '';
const randomId = 'id' + Math.random().toString(36).substring(2, 15);
/**
 * Utilities
 */
const warn = (...args) => {
    if (!debug) {
        return;
    }
    // eslint-disable-next-line no-console
    console.info(...['[meta-pixel]'].concat(args));
};
const log = (...args) => {
    if (!debug) {
        return;
    }
    // eslint-disable-next-line no-console
    console.info(...['[meta-pixel]'].concat(args));
};
// function getHourId() {
//   const now = new Date();
//   now.setMinutes(0, 0, 0);
//   const hourTimestamp = now.getTime();
//   return hourTimestamp;
// }
const verifyInit = () => {
    if (!initialized) {
        warn('Pixel not initialized before using call ReactPixel.init with required params');
    }
    return initialized;
};
//
const defaultOptions = {
    autoConfig: true,
};
let activeBrandId = '';
const brandPixels = {};
function isPixelInitialized(brandId, pixelId) {
    var _a;
    const pixels = brandPixels === null || brandPixels === void 0 ? void 0 : brandPixels[brandId];
    if ((_a = pixels === null || pixels === void 0 ? void 0 : pixels.find((item) => item.pixelId === pixelId)) === null || _a === void 0 ? void 0 : _a.initialized) {
        return true;
    }
    return false;
}
function setPixelInitialized(brandId, pixelId) {
    brandPixels[brandId] = brandPixels[brandId].map((item) => {
        if (item.pixelId === pixelId) {
            return Object.assign(Object.assign({}, item), { // keep other properties
                pixelId, initialized: true, eventFilterFn: item.eventFilter ? createPredicate(item.eventFilter) : () => true });
        }
        return item;
    });
}
const metaPixelHelper = {
    setActiveBrand(brand) {
        activeBrandId = brand;
    },
    unload() {
        const scriptElement = document.getElementById(randomId);
        if (scriptElement) {
            log('unloading fbq script ...', document.removeChild(scriptElement) ? 'success' : 'failed');
            window.fbq = undefined;
        }
    },
    setBrandPixel(brand, pixelId, eventFilter) {
        if (brand) {
            brandPixels[brand] = brandPixels[brand] || [];
            if (!brandPixels[brand].find((item) => item.pixelId === pixelId) && pixelId) {
                brandPixels[brand].push({
                    pixelId,
                    eventFilter,
                    initialized: false,
                });
            }
            else {
                // replace
                brandPixels[brand] = brandPixels[brand].map((item) => {
                    if (item.pixelId === pixelId) {
                        return Object.assign(Object.assign({}, item), { // keep other properties
                            pixelId,
                            eventFilter });
                    }
                    return item;
                });
            }
        }
    },
    getActivePixels() {
        return activeBrandId ? brandPixels[activeBrandId] : [];
    },
    initializePixel(brandId, pixelId, options) {
        if (isPixelInitialized(brandId, pixelId)) {
            return;
        }
        metaPixelHelper.init(pixelId, identity ? { external_id: identity } : {}, options || defaultOptions);
        setPixelInitialized(brandId, pixelId);
    },
    init(pixelId, advancedMatching = {}, options = defaultOptions) {
        initialized = typeof window !== 'undefined' && !!window.fbq;
        if (!initialized) {
            initialized = true;
            log('initializing fbq script');
            (function (f, b, e, v, n, t, s) {
                var _a;
                if (f.fbq)
                    return;
                n = f.fbq = function (...args) {
                    n.callMethod ? n.callMethod(...args) : n.queue.push(args);
                };
                if (!f._fbq)
                    f._fbq = n;
                n.push = n;
                n.loaded = !0;
                n.version = '2.0';
                n.queue = [];
                t = b.createElement(e);
                t.async = !0;
                t.id = randomId;
                t.src = v;
                s = b.getElementsByTagName(e)[0];
                (_a = s === null || s === void 0 ? void 0 : s.parentNode) === null || _a === void 0 ? void 0 : _a.insertBefore(t, s);
            })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
        }
        if (!pixelId) {
            warn('Please insert pixel id for initializing');
        }
        else {
            if ((options === null || options === void 0 ? void 0 : options.autoConfig) === false) {
                /*
                  The Meta Pixel will send button click and page metadata (such as data structured according to Opengraph or Schema.org formats)
                  from your website to improve your ads delivery and measurement and automate your Pixel setup. To configure the Meta Pixel to not send this additional
                  information, in the Meta Pixel Base code, add fbq('set', 'autoConfig', 'false', 'FB_PIXEL_ID') above the init call.
                */
                window.fbq('set', 'autoConfig', false, pixelId);
                log(`called fbq('set', 'autoConfig', false, '${pixelId}');`);
            }
            window.fbq('init', pixelId, advancedMatching);
            log(`called fbq('init', '${pixelId}');`);
        }
    },
    trackSingle(title, data, isCustom = false) {
        if (!verifyInit()) {
            return;
        }
        const activePixels = metaPixelHelper.getActivePixels();
        const eventType = isCustom ? 'trackSingleCustom' : 'trackSingle';
        if (activePixels && (activePixels === null || activePixels === void 0 ? void 0 : activePixels.length) > 0) {
            activePixels.forEach((pixel) => {
                if (emit) {
                    if (pixel.eventFilterFn && !pixel.eventFilterFn(data)) {
                        log('eventFilterFn failed', pixel.eventFilterFn, data);
                        return;
                    }
                    window.fbq(eventType, pixel.pixelId, title, data);
                }
                if (debug) {
                    log(`called fbq('${eventType}', '${pixel.pixelId}', '${title}'); with data `, data);
                }
            });
        }
    },
    trackSingleCustom(eventName, data) {
        return metaPixelHelper.trackSingle(eventName, data, true);
    },
};
const metaPixel = {
    enableDebug() {
        debug = true;
    },
    setIdentity(id) {
        identity = id;
    },
    enableEmitMetrics(v = true) {
        emit = v;
    },
    track({ brandId, pixels, eventName, eventType, eventPayload }) {
        pixels = pixels === null || pixels === void 0 ? void 0 : pixels.filter((item) => item.pixelProvider === PROVIDER);
        metaPixelHelper.setActiveBrand(brandId);
        if (Array.isArray(pixels) && (pixels === null || pixels === void 0 ? void 0 : pixels.length) > 0) {
            log(JSON.stringify({ brandId, pixels, eventName, payload: eventPayload }));
            if (pixels.length > 0) {
                pixels.forEach((pixel) => {
                    metaPixelHelper.setBrandPixel(brandId, pixel.pixelId, pixel.eventFilter);
                    metaPixelHelper.initializePixel(brandId, pixel.pixelId, pixel.pixelOptions);
                });
            }
            if (eventType === BrandclubEventType.OrderSubmitted || eventType === BrandclubEventType.AddToCart) {
                eventPayload = Object.assign({}, eventPayload); // clone
                if (eventPayload.retailPrice) {
                    eventPayload.value = eventPayload.retailPrice;
                    eventPayload.currency = 'USD';
                }
                eventPayload.retailPrice = undefined;
            }
            metaPixelHelper.trackSingleCustom(eventName, eventPayload);
        }
        else {
            metaPixelHelper.unload();
        }
    },
    grantConsent() {
        if (!verifyInit()) {
            return;
        }
        window.fbq('consent', 'grant');
        if (debug) {
            log(`called fbq('consent', 'grant');`);
        }
    },
    revokeConsent() {
        if (!verifyInit()) {
            return;
        }
        window.fbq('consent', 'revoke');
        if (debug) {
            log(`called fbq('consent', 'revoke');`);
        }
    },
};

export { metaPixel as default };
//# sourceMappingURL=metaPixel.js.map
